<template>
  <div class="s-microphoneWrapper">
    <button
      v-if="browserSupportsRequiredFeatures"
      ref="buttonEl"
      type="button"
      class="s-microphoneButton"
      :class="{ '-plain': props.plainStyle }"
      :aria-pressed="isRecognizing ? 'true' : 'false'"
      @click="onClickToggle"
      @pointerdown="onPointerDown"
      @pointerup="onPointerEnd"
      @pointercancel="onPointerEnd"
      @pointerleave="onPointerEnd"
      @contextmenu.prevent>
      <SvgIcon
        v-if="!showIndicator"
        class="s-microphoneButton__icon"
        :type="props.plainStyle ? 'microphone_outline' : 'microphone'"
        small />
      <VoiceDetectionIndicator
        v-else
        :current-state="indicatorState"
        :stream="microphoneStream"
        :track-audio-level="props.trackAudioLevel" />
      <div
        v-if="!props.enableVad && isRecognizing && !holdMode"
        class="s-microphoneButton__stopOverlay"
        aria-hidden="true" />
    </button>
  </div>
</template>

<script setup>
import { createPopper } from '@popperjs/core'
import {
  computed,
  onBeforeUnmount,
  ref,
} from 'vue'

import getI18n from '@/logic/i18nInstance'
import VoiceDetectionIndicator
  from '@/modules/askkeelearning/components/VoiceDetectionIndicator.vue'
import { startRecording, prepareVadModel } from '@/modules/askkeelearning/logic/speechTranscription'
import { useAudioPlayerStore } from '@/modules/global/audioPlayerStore'

const props = defineProps({
  enableVad: {
    type: Boolean,
    default: false,
  },
  plainStyle: {
    type: Boolean,
    default: false,
  },
  trackAudioLevel: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['done', 'set-listening-state'])

const isRecognizing = ref(false)
const voiceDetectionState = ref(null)

const microphoneStream = ref(null)
const stopController = ref(null)
const buttonEl = ref(null)

const audioPlayerStore = useAudioPlayerStore()

// Popper tooltip management when VAD is disabled
const popperInstance = ref(null)
let tooltipEl = null
const tooltipVisible = ref(false)
let tooltipTimer = null

function createTooltipElement(content) {
  const pop = document.createElement('div')
  pop.classList.add('c-popper', 't-body3')
  const contentEl = document.createElement('div')
  contentEl.classList.add('c-popper__content')
  contentEl.textContent = content
  pop.appendChild(contentEl)
  return pop
}

function hideTooltip() {
  if (popperInstance.value) {
    popperInstance.value.destroy()
    popperInstance.value = null
  }
  if (tooltipTimer) {
    clearTimeout(tooltipTimer)
    tooltipTimer = null
  }
  if (tooltipEl && tooltipEl.parentNode) {
    tooltipEl.parentNode.removeChild(tooltipEl)
  }
  tooltipEl = null
  tooltipVisible.value = false
}

async function showTooltip() {
  if (props.enableVad) {
    return
  }
  if (popperInstance.value || !buttonEl.value) {
    return
  }
  tooltipVisible.value = true
  const message = getI18n().t('generic.click_again_to_stop_recording')
  tooltipEl = createTooltipElement(message)
  document.body.appendChild(tooltipEl)
  popperInstance.value = createPopper(buttonEl.value, tooltipEl, {
    placement: 'bottom',
    modifiers: [
      { name: 'offset', options: { offset: [0, 10] } },
      { name: 'preventOverflow', options: { boundary: 'viewport' } },
    ],
  })
  if (tooltipTimer) {
    clearTimeout(tooltipTimer)
  }
  tooltipTimer = setTimeout(() => {
    hideTooltip()
  }, 3000)
}

const HOLD_THRESHOLD_MS = 300
let longPressTimer = null
const isPointerDown = ref(false)
const holdMode = ref(false)
const suppressNextClick = ref(false)
let lastHoldReleaseAt = 0

function clearLongPressTimer() {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

onBeforeUnmount(async () => {
  try {
    hideTooltip()
    clearLongPressTimer()
  } catch (e) {
    // ignore
  }

  // Stop any ongoing recording and release media devices
  try {
    // Manually stop all tracks to free the mic permission and resources without triggering transcription
    const stream = microphoneStream.value
    if (stream && typeof stream.getTracks === 'function') {
      const tracks = stream.getTracks()
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i]
        try {
          track.stop()
        } catch (e) {
          // ignore
        }
      }
    }
  } catch (e) {
    // ignore any teardown errors
  } finally {
    microphoneStream.value = null
    stopController.value = null
    isRecognizing.value = false
    voiceDetectionState.value = null
  }
})

// Show the indicator when:
// - VAD is enabled and we have a non-null state (including initializing/listening/processing), or
// - VAD is disabled but we are actively recording and have a stream
const showIndicator = computed(() => {
  if (props.enableVad) {
    return voiceDetectionState.value !== null
  }
  return isRecognizing.value && !!microphoneStream.value
})

// Determine which visual state to display in the indicator.
// In non-VAD mode, we show the "listening" visualization while recording.
const indicatorState = computed(() => {
  if (props.enableVad) {
    return voiceDetectionState.value
  }
  return isRecognizing.value ? 'listening' : null
})

async function startRecordingFlow({ byHold }) {
  try {
    audioPlayerStore.stop()
  } catch (e) {
    // ignore
  }
  if (props.enableVad) {
    voiceDetectionState.value = 'initializing'
  }
  stopController.value = await startRecording((stream) => {
    emit('set-listening-state', true)
    microphoneStream.value = stream
    isRecognizing.value = true
    if (props.enableVad) {
      voiceDetectionState.value = 'listening'
    } else if (!byHold) {
      // Only show tooltip for click-to-toggle
      showTooltip()
    }
  }, () => {
    isRecognizing.value = false
    microphoneStream.value = null
    stopController.value = null
    emit('set-listening-state', false)
    if (props.enableVad) {
      voiceDetectionState.value = 'processing'
    } else {
      hideTooltip()
    }
  }, (transcription, audioBlob) => {
    emit('done', { transcription, audioBlob })
    if (props.enableVad) {
      voiceDetectionState.value = null
    }
  }, { useVad: props.enableVad })
}

function stopRecordingFlow() {
  hideTooltip()
  if (stopController.value) {
    stopController.value.stopAndTranscribe()
  }
}

function onPointerDown() {
  isPointerDown.value = true
  // Do not start hold if we are already recording (click mode)
  if (isRecognizing.value) {
    return
  }
  clearLongPressTimer()
  longPressTimer = setTimeout(async () => {
    holdMode.value = true
    await startRecordingFlow({ byHold: true })
  }, HOLD_THRESHOLD_MS)
}

function onPointerEnd() {
  if (!isPointerDown.value) {
    return
  }
  isPointerDown.value = false
  clearLongPressTimer()
  if (holdMode.value) {
    stopRecordingFlow()
    holdMode.value = false
    suppressNextClick.value = true
    lastHoldReleaseAt = Date.now()
  }
  hideTooltip()
}

async function onClickToggle() {
  if (suppressNextClick.value) {
    const elapsed = Date.now() - lastHoldReleaseAt
    suppressNextClick.value = false
    if (elapsed < 250) {
      // Suppress only the immediate synthetic click after long-press release
      return
    }
    // If it's been longer, allow this real click to proceed
  }
  if (holdMode.value) {
    // Release already handled stop
    return
  }
  if (isRecognizing.value) {
    stopRecordingFlow()
    return
  }
  await startRecordingFlow({ byHold: false })
}

const browserSupportsRequiredFeatures = computed(() => {
  const supportsMediaRecording = !!(window.MediaRecorder && navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  const supportsAudioContext = typeof window.AudioContext === 'function'
  if (props.enableVad) {
    // VAD needs WebAudio (AudioContext) + getUserMedia/MediaRecorder for capturing
    return supportsMediaRecording && supportsAudioContext
  }
  // Non-VAD path only needs MediaRecorder + getUserMedia
  return supportsMediaRecording
})

if (props.enableVad) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => {
      prepareVadModel().catch(() => {})
    })
  } else {
    // Fallback if requestIdleCallback is not supported (mobile safari)
    setTimeout(() => {
      prepareVadModel().catch(() => {})
    }, 1000)
  }
}

</script>

<style lang="scss" scoped>
@import '@/assets/scss/config';
.s-microphoneButton {
  position: relative;
  width: 44px;
  height: 44px;
  border: 1px solid $color-light-gray3;
  color: $color-dark-gray2;
  padding: 0;
  background: $color-white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: $shadow-card;

  &.-plain {
    border: none;
    background: transparent;
    color: $color-dark-gray2;
  }
}
.s-microphoneButton__stopOverlay {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 12px;
  background: $color-white;
  border-radius: 2px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 2;
}
</style>
